/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */


import {ITokenProvider, ITokenResponse} from "@fluidframework/routerlicious-driver";
import {isExpiredToken} from "../utils/tokenUtils";
import {meetingPortalAuthenticatorClient} from "../../MeetingPortalAuthenticatorClient";

/**
 * Default token provider in case the host does not provide one. It simply caches the provided jwt and returns it back.
 */

export class MeetingPortalTokenProvider implements ITokenProvider {
    private fluidToken: string = '';

    constructor(
        private readonly jwt: string,
        private readonly joinMeetingId: string,
        private readonly joinMeetingPasscode: string,
        private readonly user: { id: string; name: string; },
        private readonly communicationsIdentifier: string | null,
    ) {
        this.fluidToken = jwt;
    }

    public async fetchOrdererToken(): Promise<ITokenResponse> {
        return this.fetchStorageToken();
    }

    public async fetchStorageToken(): Promise<ITokenResponse> {
        return this.getToken();
    }

    private async getToken(): Promise<ITokenResponse> {
        if (!isExpiredToken(this.fluidToken)) {
            return {
                fromCache: true,
                jwt: this.fluidToken,
            };
        }

        try {
            const response = await meetingPortalAuthenticatorClient.getMeetingDetails(
                this.joinMeetingId,
                this.joinMeetingPasscode,
                this.user,
                this.communicationsIdentifier,
            );
            this.fluidToken = response.fluidToken;
        } catch (error) {
            console.error('Could not fetch meeting details', (error as Error).message);
        }

        return {
            fromCache: false,
            jwt: this.fluidToken,
        };
    }
}
